import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedCallback } from "use-debounce";

import { type MealsPlanWithCreator } from "@fitness-app/data-models/entities/MealsPlan";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

export interface MealsPlanTemplateFilter {
  /** Only templates the signed-in user created. */
  onlyMine: boolean;
  /** Any of these `meals_plan.tags`. */
  tags: string[];
}

export const DEFAULT_MEALS_PLAN_TEMPLATE_FILTER: MealsPlanTemplateFilter = { onlyMine: true, tags: [] };

const PAGE_SIZE = 50;
const SELECT = "*, creator:createdBy(id, firstName, lastName)";

const byName = (a: MealsPlanWithCreator, b: MealsPlanWithCreator) => a.name.localeCompare(b.name, "pl");

/**
 * One meals_plan template search for every place a trainer picks a diet template: the weight-range
 * schedule and the AI diet's reference plans. Filters by author and tags, searches by name, and keeps
 * already-saved ids selectable even when the current page does not contain them.
 */
export const useMealsPlanTemplateSearch = (filter: MealsPlanTemplateFilter, savedIds: string[] = []) => {
  const { userId } = useUserRole();
  const [searchValue, setSearchValue] = useState("");
  // Everything seen so far, so a submit can resolve an id picked before the filters changed.
  const plansById = useRef<Record<string, MealsPlanWithCreator>>({});

  const remember = (plans: MealsPlanWithCreator[]) => {
    for (const plan of plans) {
      plansById.current[plan.id] = plan;
    }
    return plans;
  };

  const page = useQuery(
    ["searchMealsPlanTemplates", filter.onlyMine ? userId : null, [...filter.tags].sort(), searchValue],
    {
      keepPreviousData: true,
      queryFn: async () => {
        let query = supabase.from("meals_plan").select(SELECT);

        if (filter.onlyMine) {
          query = query.eq("createdBy", userId);
        }

        if (searchValue.length > 0) {
          query = query.ilike("name", `%${searchValue}%`);
        }

        if (filter.tags.length) {
          query = query.or(filter.tags.map((tag) => `tags.cs.["${tag}"]`).join(","));
        }

        const { data } = await query
          .order("createdAt", { ascending: false })
          .range(0, PAGE_SIZE - 1)
          .returns<MealsPlanWithCreator[]>()
          .throwOnError();

        return remember(data);
      },
    },
  );

  const missingSavedIds = useMemo(
    () => savedIds.filter((id) => !plansById.current[id] && !(page.data ?? []).some((plan) => plan.id === id)),
    [savedIds, page.data],
  );

  const saved = useQuery(["mealsPlanTemplatesByIds", [...missingSavedIds].sort()], {
    enabled: missingSavedIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from("meals_plan")
        .select(SELECT)
        .in("id", missingSavedIds)
        .returns<MealsPlanWithCreator[]>()
        .throwOnError();

      return remember(data);
    },
  });

  const plans = useMemo(() => {
    const pagePlans = page.data ?? [];
    const pageIds = new Set(pagePlans.map((plan) => plan.id));
    const savedPlans = savedIds
      .map((id) => plansById.current[id])
      .filter((plan): plan is MealsPlanWithCreator => !!plan && !pageIds.has(plan.id));

    return [...pagePlans, ...savedPlans].sort(byName);
    // saved.data only feeds plansById; it is listed so a late arrival re-renders the list.
  }, [page.data, saved.data, savedIds]);

  const onSearch = useDebouncedCallback((value: string) => setSearchValue(value), 300);

  return { plans, plansById: plansById.current, isLoading: page.isLoading, onSearch };
};
